/* eslint-disable */
import memoize from 'lodash-es/memoize'
// import Big from 'big.js'

const DECIMAL_PLACES_MATCHER = /(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/

export const DASH = '–'
const POSITIVE_SYMBOL = '+'
const NEGATIVE_SYMBOL = '-'

// memoize for performance, avoids creating a RegExp each call
const getFormatNumberRegexp = memoize(
  (n, x) =>
    // eslint-disable-next-line prefer-template
    new RegExp('\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\.' : '$') + ')', 'g'),
  (...args) => args.join(',')
)

export const equal = (x, y) => Math.abs(x - y) < Number.EPSILON

/**
 * Format a number with comma separated sections.
 *
 * @param {number | string} num the number to format
 * @param {integer} n length of decimal
 * @param {integer} x length of sections
 */
export const formatNumber = (num, n, x = 3) => {
  if (typeof num !== 'number' && !num) return '-'
  num = typeof num === 'number' ? num : +num
  const re = getFormatNumberRegexp(n, x)
  return num.toFixed(Math.max(0, ~~n)).replace(re, '$&,')
}

/**
 * Add positive or negative indicator (+ or -) to a number.
 *
 * @param {number | string} num the number to format
 * @param {showPos} show plus `+` when positive?
 */
export const addPosNeg = (num, showPos = true) =>
  !equal(+num, 0) && +num > 0 ? `${showPos ? '+' : ''}${num}` : `${num}`

/**
 * Gets the rounding function for the type of trade.
 * See original code at https://git.io/vxJDj
 *
 * @param {string} type the type  of trade (bid, ask)
 */
export const getRoundFunc = (type = 'bids') =>
  (() => {
    switch (type) {
      case 'bids':
        return 'floor'
      case 'asks':
        return 'ceil'
      default:
        if (Math[type]) return Math[type]
        throw new Error('getRoundFunc called with unknown type')
    }
  })()

/**
 * Round a decimal number to the specified precision.
 *
 * @param {number | string} input
 * @param {number} precision
 * @param {function} func the rounding function
 * @param {bool} format should format?
 */
export const decRound = (input, precision, func = 'round', format = false) => {
  const factor = 10 ** precision
  // better browser support epsilon
  // see unit tests.
  const adj = 3
  let num = (+input + 1 / 10 ** (precision + adj)) * factor
  if (typeof func === 'string') {
    const dps = adj - 1
    num = parseFloat(num * 10 ** dps, 10) / 10 ** dps
    func = getRoundFunc(func)
  }
  if (typeof func !== 'function') throw new Error('decRound unknown rounding func')
  if (format) {
    return formatNumber(func(num) / factor, precision)
  }
  return (func(num) / factor).toFixed(precision)
}

export const floor = (num, digit) => decRound(num, digit, 'floor')

export const ceil = (num, digit) => decRound(num, digit, 'ceil')

/**
 * Returns the number of decimal places in a number.
 *
 * @param {number | string} num
 */
export const decimalPlaces = num => {
  const match = `${num}`.match(DECIMAL_PLACES_MATCHER)
  if (!match) return 0

  let matchValue = match[1]
  if (matchValue && matchValue.endsWith(0)) {
    const index = matchValue.indexOf(1)
    matchValue = matchValue.substring(0, index + 1)
  }
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (matchValue ? matchValue.length : 0) -
      // Adjust for scientific notation.
      (match[2] ? +match[2] : 0)
  )
}

export const roundValue = (value, precision) => {
  if (value === '') return ''
  if (Number.isInteger(value)) return value
  precision = precision || '0.00000001'
  const ts = decimalPlaces(precision)

  //parseFloat is used to avoid 0.00000
  value = parseFloat(floor(value, ts))

  value = value < 0.000009 ? toNonExponential(value) : value
  return value
}

export const multiply = (...args) => {
  const nums = args.map(n => Number(n))
  let factorsSum = 1
  const factors = nums.map(n => {
    const decimals = decimalPlaces(n)
    factorsSum *= 10 ** decimals
    return 10 ** decimals * n
  })
  const product = factors.reduce((n, pre) => pre * n, 1)
  if (product > Number.MAX_SAFE_INTEGER) {
    return nums.reduce((n, pre) => n * pre, 1)
  }
  return product / factorsSum
}

/**
 * Returns the string with leading zeros.
 *
 * @param {number} num
 * @param {number} size
 * @return {string}
 */
export const pad = (num, size = 2) => {
  let str = String(num)
  while (str.length < size) {
    str = `0${str}`
  }
  return str
}

/**
 * Returns nonExponential
 * @param {Number|String} num
 */
export const toNonExponential = num => {
  num = parseFloat(num)
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/)
  return num.toFixed(Math.max(0, (m[1] || '').length - m[2]))
}

const getPencentageSymbol = (numerator, denominator) => {
  if (numerator > 0) {
    if (denominator > 0) {
      return POSITIVE_SYMBOL
    }
    return NEGATIVE_SYMBOL
  } else if (numerator < 0) {
    if (denominator > 0) {
      return NEGATIVE_SYMBOL
    }
    return POSITIVE_SYMBOL
  }
  return ''
}

export const pencentage = (numerator, denominator, config = { digit: 2, withSymbol: false }) => {
  const { digit = 2, withSymbol = false } = config
  const symbol = withSymbol ? getPencentageSymbol(numerator, denominator) : ''
  return denominator === 0
    ? DASH
    : `${symbol}${decRound(Math.abs(numerator / denominator) * 100, digit)}%`
}

/**
 * format price.
 * @param {number} price
 * @return {number}
 */
export const calPrice = (price, isCoin) => {
  if (!price.toFixed) return price
  if (!price) return '-'
  if (price < 1) {
    return isCoin ? price.toFixed(8) : price.toFixed(6)
  }
  return price.toFixed(2)
}

export const getRandomInt = max => {
  return Math.floor(Math.random() * Math.floor(max))
}

export default {
  getRoundFunc,
  decRound,
  formatNumber,
  decimalPlaces,
  pad,
  floor,
  ceil,
}
